import { useState, useEffect, useContext } from 'react';

import { IconBell, IconChevronDown } from '@tabler/icons-react';
import { useNotifications } from 'ui-kit/hooks/useNotifications';
import { AuthContext } from 'context/AuthContext';
import { getUserAttributes } from 'utils/userAttributes';
import { UserModel } from 'features/pages/account/User';
import { Route, Routes } from 'react-router-dom';
import { Avatar, DraggableHelpButton } from 'ui-kit';
import { FloatingFocusManager, FloatingPortal, usePopout } from 'ui-kit/hooks/usePopout';
import NotificationsDropdown from 'ui-kit/Notifications/NotificationsDropdown';
import { JobsDropdown } from 'features/jobs/JobsDropdown';

import { useLazyQuery } from '@apollo/client';

import { HeaderLogo } from './HeaderLogo';
import { MAIN_HEADER_CONFIG } from './headers.config';
import { MobileNavDrawer } from './MobileNavDrawer';
import { useMediaQuery, WIDE_ENOUGH_FOR_INLINE_NAV } from './useMediaQuery';
import ReferralLink from './ReferralLink';
import { SubHeader } from './SubHeader';
import { UserContextMenu } from './UserContextMenu';
import {
  DataContext,
  DataContextActionType,
  initGlobalTableOrderConfig,
} from '../../../context/DataContext';
import { checkColumnCfgPayload } from '../../../hooks/useColumnOrder';
import { GetUserSetting } from '../../../query/__generated__/userData';
import { GET_USER_SETTING } from '../../../query/userData';
import HeaderMenuItem from '../HeaderMenuItem/HeaderMenuItem';

const Header = () => {
  const { state: authState } = useContext(AuthContext);
  const { dispatch } = useContext(DataContext);
  const [userInfo, setUserInfo] = useState<UserModel>();
  const [blinkBell, setBlinkBell] = useState(false);
  // role: 'dialog', not 'menu': the ARIA menu pattern requires role="menuitem"
  // children and arrow-key navigation between them, neither of which either
  // panel implements -- UserContextMenu's items are plain links/buttons
  // reachable only by Tab, and NotificationsDropdown is a feed of rows, not
  // a list of commands. 'dialog' matches what's actually here: a
  // self-contained popup navigated by Tab, dismissed by Escape or an
  // outside click.
  const notifPopout = usePopout({ placement: 'bottom-end', offset: 4, role: 'dialog' });
  const userMenuPopout = usePopout({ placement: 'bottom-end', offset: 4, role: 'dialog' });

  const [getUserSetting] = useLazyQuery<GetUserSetting>(GET_USER_SETTING, {
    fetchPolicy: 'network-only',
  });

  const getUserTableSettings = async () => {
    try {
      const { data } = await getUserSetting({
        variables: { name: 'globalTableOrderConfig' },
      });

      if (data) {
        dispatch({
          type: DataContextActionType.SET_GLOBAL_TABLE_ORDER_CONFIG,
          payload: checkColumnCfgPayload(JSON.parse(data?.user.setting)),
        });
      }
    } catch {}
  };

  useEffect(() => {
    let isMounted = true;
    if (authState.user) {
      getUserTableSettings();
      getUserAttributes()
        .then((attributes) => {
          if (isMounted) {
            setUserInfo({
              givenName: attributes.given_name || '',
              familyName: attributes.family_name || '',
              email: attributes.email || '',
              phone: attributes.phone_number || '',
              title: '',
              company: authState.company?.name || '',
              address: {
                address1: '',
                suite: '',
                city: '',
                state: '',
                zipcode: '',
              },
              roles: '',
            });
          }
        })
        .catch((err) => {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to get user attributes from token:', err);
          }
        });
    }
    return () => {
      isMounted = false;
    };
  }, [authState.user, authState.company]);

  const isWide = useMediaQuery(WIDE_ENOUGH_FOR_INLINE_NAV);
  const displayName = `${userInfo?.givenName ?? ''} ${userInfo?.familyName ?? ''}`.trim() || 'User';

  const { notifications, loading } = useNotifications();
  const hasUnreadNotifications =
    notifications.filter((item) => Number(item.notificationMetadata.readAt) <= 0).length > 0;

  useEffect(() => {
    if (hasUnreadNotifications) {
      setBlinkBell(true);
      const timer = setTimeout(() => setBlinkBell(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [hasUnreadNotifications]);

  return (
    <header className="border-b border-slate-500 bg-slate-950 light:border-slate-100 light:bg-white">
      <nav className="text-white light:text-gray-600">
        <div className="flex items-end pb-3 pt-4 md:pb-0">
          <div className="flex flex-none items-center gap-2 self-center pl-3 md:w-[max(186px,calc(100%/6_-_54px))] md:pl-5 [&_img]:h-5 md:[&_img]:h-6">
            <MobileNavDrawer />
            <HeaderLogo />
          </div>
          <div className="hidden h-full min-w-0 flex-1 items-end md:flex">
            <ul className="m-0 flex min-w-0 p-0">
              {MAIN_HEADER_CONFIG.map((headerMenuItem) => {
                return (
                  <HeaderMenuItem
                    key={headerMenuItem.routeKey}
                    tabDetails={headerMenuItem}
                    isTourAnchor={isWide}
                  />
                );
              })}
            </ul>
          </div>
          <div className="ml-auto mr-3 flex flex-none items-center justify-end gap-2 self-center pl-2 md:mr-5 md:gap-5">
            <JobsDropdown />
            <div className="relative">
              <button
                type="button"
                aria-label="Notifications"
                ref={notifPopout.refs.setReference}
                {...notifPopout.getReferenceProps()}
                className="flex items-center rounded bg-gray-950 px-2 py-2.5 text-white light:bg-gray-100 light:text-gray-900 md:px-3"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full">
                  <IconBell />
                </span>
              </button>
              {notifPopout.isOpen && (
                <FloatingPortal>
                  <FloatingFocusManager context={notifPopout.context} modal={false}>
                    <div
                      ref={notifPopout.refs.setFloating}
                      style={notifPopout.floatingStyles}
                      {...notifPopout.getFloatingProps()}
                      // role="dialog" (from usePopout's role: 'dialog') needs
                      // an accessible name or a screen reader just announces
                      // an unnamed dialog -- no visible heading in this panel
                      // to aria-labelledby off of, so aria-label directly.
                      aria-label="Notifications"
                      // text-white: NotificationsDropdown's "No Notifications"
                      // empty state is bare text with no color class of its own
                      // (unlike its populated state, which sets font-heebo
                      // explicitly on every row) -- it relied on inheriting
                      // <nav>'s text-white, which a FloatingPortal, mounting
                      // this subtree outside <nav> in the DOM, doesn't carry
                      // across.
                      // z-40: above pill remove-badges (z-30), below the app's
                      // modal/dialog tier (z-50) -- same convention every ui-kit
                      // dropdown/flyout in this app now uses.
                      className="z-40 overflow-hidden rounded-md bg-gray-950 text-white shadow-[0px_8px_16px_0px_rgba(13,13,13,0.9)] light:border light:border-gray-200 light:bg-white light:text-gray-900 light:shadow-lg"
                    >
                      <NotificationsDropdown
                        notifications={notifications}
                        loading={loading}
                        className="max-h-[500px] w-[400px] bg-background-surface light:bg-white"
                      />
                    </div>
                  </FloatingFocusManager>
                </FloatingPortal>
              )}
              {hasUnreadNotifications && (
                <span
                  className={`absolute right-[10px] top-[10px] block h-2 w-2 rounded-full bg-blue-400 transition-opacity duration-300 ${blinkBell ? 'animate-ping' : ''}`}
                ></span>
              )}
            </div>
            <div>
              <button
                type="button"
                aria-label={`${displayName}, account menu`}
                ref={userMenuPopout.refs.setReference}
                {...userMenuPopout.getReferenceProps()}
                className="flex items-center rounded bg-gray-950 px-2 py-2.5 text-xs text-white light:bg-gray-100 light:text-gray-900 md:px-3"
              >
                <span className="sm:mr-1 xl:mr-2">
                  <Avatar name={displayName} width="33px" height="33px" fontSize="18px" />
                </span>
                <span className="mr-8 hidden flex-col items-start text-left leading-tight xl:flex">
                  <span>{displayName}</span>
                  {userInfo?.company && (
                    <span className="text-xxs font-normal text-slate-400 light:text-gray-700">
                      {userInfo.company}
                    </span>
                  )}
                </span>
                <span className="hidden sm:inline-flex">
                  <IconChevronDown />
                </span>
              </button>
              {userMenuPopout.isOpen && (
                <FloatingPortal>
                  <FloatingFocusManager context={userMenuPopout.context} modal={false}>
                    <div
                      ref={userMenuPopout.refs.setFloating}
                      style={userMenuPopout.floatingStyles}
                      {...userMenuPopout.getFloatingProps()}
                      // Same reasoning as the notifications panel: role="dialog"
                      // needs an accessible name, and there's no visible
                      // heading here to aria-labelledby off of.
                      aria-label="Account menu"
                      // z-40: same "above pill badge, below modal tier"
                      // convention as the rest of this series.
                      className="z-40"
                    >
                      <UserContextMenu userInfo={userInfo} />
                    </div>
                  </FloatingFocusManager>
                </FloatingPortal>
              )}
            </div>
          </div>
        </div>
        <Routes>
          <Route path=":mainSegment/*" element={<SubHeader />} />
        </Routes>
        <div className="absolute right-5 top-[71px]">
          <ReferralLink />
        </div>
        <DraggableHelpButton />
      </nav>
    </header>
  );
};

export default Header;
